import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    AlertTriangle,
    ArrowLeft,
    Calendar,
    ChevronLeft,
    ChevronRight,
    Heart,
    Mail,
    MapPin,
    MessageSquare,
    Package,
    Shield,
    Star,
    Tag,
    User,
    XCircle
} from 'lucide-react';
import { useUser } from '../../../../UserContext';
import './ListingDetail.css';
import ShareMenuButton from '../../../../components/share/ShareMenuButton';
import { clubsService, marketplaceService, socialService } from '../../../../services';
import { getClubPublicPath } from '../../../../utils/clubPublicPaths';

const ListingDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();
    const [listing, setListing] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isFavourited, setIsFavourited] = useState(false);
    const [showOfferModal, setShowOfferModal] = useState(false);
    const [offerAmount, setOfferAmount] = useState('');
    const [offerMessage, setOfferMessage] = useState('');
    const [isSubmittingOffer, setIsSubmittingOffer] = useState(false);
    const [nearbyClubs, setNearbyClubs] = useState([]);
    const [isLoadingNearbyClubs, setIsLoadingNearbyClubs] = useState(false);
    const [nearbyPerks, setNearbyPerks] = useState([]);
    const [isLoadingNearbyPerks, setIsLoadingNearbyPerks] = useState(false);

    const fetchListing = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const data = await marketplaceService.getListing(id);
            setListing(data);
            setIsFavourited(Boolean(data.isFavourited));
        } catch (fetchError) {
            console.error('Error fetching listing:', fetchError);
            setError(fetchError.message || 'Failed to fetch listing');
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchListing();
    }, [fetchListing]);

    useEffect(() => {
        const fetchNearbyClubs = async () => {
            const lookupLocation = listing?.postCode || listing?.location;
            if (!lookupLocation) {
                setNearbyClubs([]);
                return;
            }

            setIsLoadingNearbyClubs(true);
            try {
                const params = new URLSearchParams();
                params.append('location', lookupLocation);
                const clubResults = await clubsService.advancedSearch(params);
                setNearbyClubs((clubResults || []).slice(0, 4));
            } catch (lookupError) {
                console.error('Error fetching nearby clubs for listing:', lookupError);
                setNearbyClubs([]);
            } finally {
                setIsLoadingNearbyClubs(false);
            }
        };

        if (listing) {
            fetchNearbyClubs();
        }
    }, [listing]);

    useEffect(() => {
        const fetchNearbyPerks = async () => {
            if (!nearbyClubs.length) {
                setNearbyPerks([]);
                return;
            }

            setIsLoadingNearbyPerks(true);
            try {
                const params = new URLSearchParams();
                params.append('take', '24');
                params.append('audience', 'Public');
                const offers = await clubsService.getLatestOffers(params);
                const nearbyClubIds = new Set(nearbyClubs.map((club) => club.clubId));
                const filtered = (offers || []).filter((offer) => nearbyClubIds.has(offer.clubId)).slice(0, 6);
                setNearbyPerks(filtered);
            } catch (perksError) {
                console.error('Error fetching nearby perks for listing:', perksError);
                setNearbyPerks([]);
            } finally {
                setIsLoadingNearbyPerks(false);
            }
        };

        fetchNearbyPerks();
    }, [nearbyClubs]);

    const handleFavourite = async () => {
        try {
            await marketplaceService.toggleFavourite(id);
            setIsFavourited((current) => !current);
        } catch (toggleError) {
            console.error('Error toggling favourite:', toggleError);
        }
    };

    const handleShare = async (platform) => {
        try {
            const sharePlatform = platform === 'Copy' ? 'CopyLink' : platform;
            const data = await socialService.shareListing(Number(id), sharePlatform);
            if (!data?.shareUrl) return;

            if (platform === 'Copy') {
                await navigator.clipboard.writeText(data.shareUrl);
                return;
            }

            window.open(data.shareUrl, '_blank', 'noopener,noreferrer,width=700,height=550');
        } catch (shareError) {
            console.error('Error sharing listing:', shareError);
        }
    };

    const handleMakeOffer = async (event) => {
        event.preventDefault();
        setIsSubmittingOffer(true);

        try {
            await marketplaceService.createOffer({
                listingId: Number(id),
                offerAmount: parseFloat(offerAmount),
                message: offerMessage
            });

            setShowOfferModal(false);
            setOfferAmount('');
            setOfferMessage('');
            alert('Offer submitted successfully.');
        } catch (offerError) {
            alert(offerError.message || 'Failed to submit offer');
        } finally {
            setIsSubmittingOffer(false);
        }
    };

    const images = useMemo(
        () => listing?.imageUrls?.length > 0 ? listing.imageUrls : ['/placeholder-equipment.jpg'],
        [listing]
    );

    const isOwnListing = listing?.sellerId === user?.userId;
    const certificateType = listing?.firearmSection === 'Section1'
        ? 'FAC'
        : listing?.firearmSection === 'Section2'
            ? 'SGC'
            : null;
    const isPremiumSeller = Number(listing?.platformFeePercent) <= 2.5;

    const nextImage = () => {
        if (images.length > 1) {
            setCurrentImageIndex((previous) => (previous + 1) % images.length);
        }
    };

    const prevImage = () => {
        if (images.length > 1) {
            setCurrentImageIndex((previous) => (previous - 1 + images.length) % images.length);
        }
    };

    const getConditionClass = (condition) => {
        const classes = {
            New: 'condition-new',
            'Like New': 'condition-like-new',
            Excellent: 'condition-excellent',
            Good: 'condition-good',
            Fair: 'condition-fair',
            Poor: 'condition-poor'
        };

        return classes[condition] || 'condition-good';
    };

    if (isLoading) {
        return (
            <div className="listing-detail-loading">
                <div className="loading-spinner"></div>
                <p>Loading listing...</p>
            </div>
        );
    }

    if (error || !listing) {
        return (
            <div className="listing-detail-error">
                <XCircle size={48} />
                <h2>Listing Not Found</h2>
                <p>{error || 'This listing may have been removed or is no longer available.'}</p>
                <button onClick={() => navigate('/dashboard/marketplace')} className="btn-back">
                    Back to Marketplace
                </button>
            </div>
        );
    }

    return (
        <div className="listing-detail-page">
            <button className="back-button" onClick={() => navigate('/dashboard/marketplace')}>
                <ArrowLeft size={20} />
                Back to Marketplace
            </button>

            {listing.requiresCertificateVerification && (
                <div className="compliance-banner">
                    <AlertTriangle size={24} />
                    <div className="compliance-content">
                        <h3>Firearm or ammunition listing</h3>
                        <p>
                            This item requires a valid {certificateType === 'FAC' ? 'Firearms Certificate (FAC)' : 'Shotgun Certificate (SGC)'}.
                            Collection only. The buyer must present a valid certificate before transfer.
                        </p>
                    </div>
                </div>
            )}

            <div className="listing-detail-content">
                <div className="listing-gallery">
                    <div className="main-image-container">
                        <img src={images[currentImageIndex]} alt={listing.title} className="main-image" />
                        {images.length > 1 && (
                            <>
                                <button className="gallery-nav prev" onClick={prevImage}>
                                    <ChevronLeft size={24} />
                                </button>
                                <button className="gallery-nav next" onClick={nextImage}>
                                    <ChevronRight size={24} />
                                </button>
                            </>
                        )}
                        <span className={`condition-badge ${getConditionClass(listing.condition)}`}>
                            {listing.condition}
                        </span>
                        {listing.requiresCertificateVerification && (
                            <span className="certificate-badge">
                                <Shield size={14} />
                                {certificateType}
                            </span>
                        )}
                    </div>
                    {images.length > 1 && (
                        <div className="thumbnail-strip">
                            {images.map((imageUrl, index) => (
                                <button
                                    key={imageUrl}
                                    className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                                    onClick={() => setCurrentImageIndex(index)}
                                >
                                    <img src={imageUrl} alt={`Thumbnail ${index + 1}`} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="listing-info">
                    <div className="listing-header">
                        <div className="listing-title-section">
                            <span className="listing-category">{listing.category}</span>
                            <h1>{listing.title}</h1>
                            {(listing.make || listing.model) && (
                                <p className="listing-brand">{listing.make || ''} {listing.model ? `- ${listing.model}` : ''}</p>
                            )}
                        </div>
                        {!isOwnListing && (
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <ShareMenuButton onShare={handleShare} />
                                <button className={`favourite-btn ${isFavourited ? 'favourited' : ''}`} onClick={handleFavourite}>
                                    <Heart size={24} fill={isFavourited ? '#F26522' : 'none'} />
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="listing-price-section">
                        <div className="price-display">
                            <span className="price">£{listing.price?.toLocaleString()}</span>
                            <span className="accepts-offers">
                                <Tag size={16} />
                                {listing.listingType || 'Sale'}
                            </span>
                        </div>
                    </div>

                    <div className="listing-meta">
                        <div className="meta-item">
                            <MapPin size={18} />
                            <span>{listing.location || 'Location not specified'}</span>
                        </div>
                        <div className="meta-item">
                            <Calendar size={18} />
                            <span>Listed {new Date(listing.createdAt).toLocaleDateString('en-GB')}</span>
                        </div>
                        <div className="meta-item">
                            <Package size={18} />
                            <span>{listing.collectionOnly ? 'Collection only' : 'Delivery available'}</span>
                        </div>
                    </div>

                    <div className="listing-description">
                        <h3>Description</h3>
                        <p>{listing.description}</p>
                    </div>

                    <div className="listing-nearby-clubs">
                        <div className="listing-nearby-header">
                            <h3>Nearby venues</h3>
                            <button className="btn-secondary" onClick={() => navigate('/Clubs')}>
                                Browse venues
                            </button>
                        </div>
                        {isLoadingNearbyClubs ? (
                            <p className="listing-nearby-muted">Finding venues near this listing...</p>
                        ) : nearbyClubs.length === 0 ? (
                            <p className="listing-nearby-muted">No nearby venues were found for this listing location yet.</p>
                        ) : (
                            <div className="listing-nearby-grid">
                                {nearbyClubs.map((club) => (
                                    <button
                                        type="button"
                                        key={club.clubId}
                                        className="listing-nearby-card"
                                        onClick={() => navigate(getClubPublicPath(club))}
                                    >
                                        <strong>{club.name}</strong>
                                        <span>{club.address}</span>
                                        {club.distance ? <em>{club.distance.toFixed(1)} km away</em> : null}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="listing-nearby-clubs listing-nearby-perks">
                        <div className="listing-nearby-header">
                            <h3>Perks near this listing</h3>
                            <button
                                className="btn-secondary"
                                onClick={() => navigate('/dashboard/connect-perks', { state: { fromDashboard: true, from: `/dashboard/marketplace/${id}` } })}
                            >
                                View Connect Perks
                            </button>
                        </div>
                        {isLoadingNearbyPerks ? (
                            <p className="listing-nearby-muted">Finding perks from nearby venues...</p>
                        ) : nearbyPerks.length === 0 ? (
                            <p className="listing-nearby-muted">No nearby public perks found right now.</p>
                        ) : (
                            <div className="listing-nearby-grid">
                                {nearbyPerks.map((offer) => (
                                    <button
                                        type="button"
                                        key={offer.clubOfferId}
                                        className="listing-nearby-card"
                                        onClick={() => navigate(getClubPublicPath(offer))}
                                    >
                                        <strong>{offer.title}</strong>
                                        <span>{offer.clubName}</span>
                                        {offer.endDate ? <em>Ends {new Date(offer.endDate).toLocaleDateString('en-GB')}</em> : null}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="seller-card">
                        <div className="seller-header">
                            <div className="seller-avatar">
                                {listing.sellerProfilePicture ? (
                                    <img src={listing.sellerProfilePicture} alt={listing.sellerName} />
                                ) : (
                                    <User size={32} />
                                )}
                            </div>
                            <div className="seller-info">
                                <h4>{listing.sellerName}</h4>
                                <p className="member-since" style={{ marginTop: '4px' }}>
                                    {listing.sellerType === 'Venue' ? 'Venue seller' : 'Member seller'}
                                </p>
                                <div className="seller-stats">
                                    {listing.sellerRating > 0 && (
                                        <span className="seller-rating">
                                            <Star size={14} fill="#FFB800" />
                                            {listing.sellerRating.toFixed(1)}
                                        </span>
                                    )}
                                </div>
                                <p className="member-since">{listing.sellerTotalSales || 0} completed sales</p>
                                {listing.sellerType === 'Venue' && listing.clubId ? (
                                    <>
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            style={{ marginTop: '8px' }}
                                            onClick={() => navigate(getClubPublicPath(listing))}
                                        >
                                            View venue profile
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            style={{ marginTop: '8px' }}
                                            onClick={() => navigate(`/dashboard/marketplace/venue/${listing.clubId}`)}
                                        >
                                            View all venue listings
                                        </button>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    <div className="listing-actions">
                        {!isOwnListing ? (
                            <>
                                <button className="btn-primary" onClick={() => setShowOfferModal(true)}>
                                    <MessageSquare size={20} />
                                    Make an Offer
                                </button>
                                <button className="btn-secondary" onClick={() => navigate('/dashboard/marketplace/my-listings')}>
                                    <Mail size={20} />
                                    Marketplace Hub
                                </button>
                            </>
                        ) : (
                            <button className="btn-primary" onClick={() => navigate('/dashboard/marketplace/my-listings')}>
                                Manage Listing
                            </button>
                        )}
                    </div>

                    <div className="fee-notice">
                        <p>
                            <strong>Platform Fee:</strong> A {isPremiumSeller ? '2.5%' : `${listing.platformFeePercent}%`} fee applies to completed transactions.
                            {isPremiumSeller && <span className="premium-discount"> Premium member discount applied.</span>}
                        </p>
                    </div>
                </div>
            </div>

            {showOfferModal && (
                <div className="offer-modal-overlay" onClick={() => setShowOfferModal(false)}>
                    <div className="offer-modal" onClick={(event) => event.stopPropagation()}>
                        <h3>Make an Offer</h3>
                        <p>Submit an offer for {listing.title}</p>
                        <form onSubmit={handleMakeOffer}>
                            <label>
                                Offer Amount
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={offerAmount}
                                    onChange={(event) => setOfferAmount(event.target.value)}
                                    required
                                />
                            </label>
                            <label>
                                Message
                                <textarea
                                    value={offerMessage}
                                    onChange={(event) => setOfferMessage(event.target.value)}
                                    rows={4}
                                    placeholder="Add any collection or certificate notes here"
                                />
                            </label>
                            <div className="offer-modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setShowOfferModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary" disabled={isSubmittingOffer}>
                                    {isSubmittingOffer ? 'Submitting...' : 'Send Offer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ListingDetail;
